<template>
    <div class="ui-page">
        <header class="ui-page-header">
            <router-link to="/adesao-plano" class="ui-eyebrow"><v-icon small>mdi-arrow-left</v-icon> Adesão ao Plano</router-link>
            <div class="ui-page-header__row">
                <div class="ui-page-header__text">
                    <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                        <h1 class="ui-title">{{ campaign && campaign.client ? campaign.client.name : "Carregando..." }}</h1>
                        <ui-badge set="adhesion" v-if="campaign" :status="campaign.status" />
                    </div>
                    <p v-if="campaign" class="ui-subtitle">
                        <span v-if="campaign.client.process_number">Processo {{ campaign.client.process_number }} · </span>{{ windowText }}
                    </p>
                </div>
                <div v-if="campaign" class="d-flex align-center" style="gap: 8px;">
                    <v-btn v-if="campaign.status !== 'open' && $allowed('plan.campaign')" color="primary" depressed :loading="busy === 'open'" @click="confirmOpen">
                        <v-icon left small>mdi-play</v-icon> Abrir adesão
                    </v-btn>
                    <v-btn v-else-if="campaign.status === 'open' && $allowed('plan.campaign')" outlined class="ui-btn-danger" :loading="busy === 'close'" @click="confirmClose">
                        Encerrar adesão
                    </v-btn>
                </div>
            </div>
            <div v-if="campaign" class="ui-steps mt-3">
                <span v-for="step in checklist" :key="step.label" class="ui-step" :class="{ 'ui-step--done': step.done }">
                    <v-icon :color="step.done ? 'success' : '#a1a1aa'">{{ step.done ? "mdi-check-circle" : "mdi-circle-outline" }}</v-icon>
                    {{ step.label }}
                </span>
                <span v-if="campaign.status === 'open' && !campaign.accepting_terms" class="ui-step" style="color: #b45309;">
                    <v-icon color="#d97706">mdi-alert-circle-outline</v-icon> Fora da janela configurada
                </span>
            </div>
        </header>

        <template v-if="campaign">
            <v-tabs v-model="tab" show-arrows>
                <v-tab>Configuração</v-tab>
                <v-tab>Plano<span v-if="pendingPlans" class="ui-tab-count ui-tab-count--attention">{{ pendingPlans }}</span></v-tab>
                <v-tab>Termo<span v-if="pendingTemplates" class="ui-tab-count ui-tab-count--attention">{{ pendingTemplates }}</span></v-tab>
                <v-tab>Adesões<span v-if="campaign.pending_terms" class="ui-tab-count ui-tab-count--attention">{{ campaign.pending_terms }}</span></v-tab>
                <v-tab>Analítico</v-tab>
                <v-tab @click="loadAudit(1)">Auditoria</v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab" class="pt-5">
                <v-tab-item>
                    <v-row>
                        <v-col cols="12" md="6">
                            <ui-section title="Janela de adesão" description="Horário de Brasília. Sem encerramento, a adesão fica aberta até ser encerrada manualmente.">
                                <v-text-field v-model="config.opens_at" type="datetime-local" label="Abertura" outlined dense hide-details="auto" class="mb-4"></v-text-field>
                                <v-text-field v-model="config.closes_at" type="datetime-local" label="Encerramento (opcional)" outlined dense clearable hide-details="auto"></v-text-field>
                            </ui-section>
                        </v-col>
                        <v-col cols="12" md="6">
                            <ui-section title="Cotação para o quórum por valor" description="Usada só para somar créditos em moeda estrangeira nas classes com maioria de valor. Os gráficos seguem separados por moeda.">
                                <v-row dense>
                                    <v-col cols="6"><v-text-field v-model="config.exchange_rate_usd" label="USD → BRL" outlined dense hide-details="auto" placeholder="5,4321"></v-text-field></v-col>
                                    <v-col cols="6"><v-text-field v-model="config.exchange_rate_eur" label="EUR → BRL" outlined dense hide-details="auto" placeholder="5,9876"></v-text-field></v-col>
                                    <v-col cols="6"><v-text-field v-model="config.exchange_rate_date" type="date" label="Data da cotação" outlined dense hide-details="auto"></v-text-field></v-col>
                                    <v-col cols="6"><v-text-field v-model="config.exchange_rate_source" label="Fonte" outlined dense hide-details="auto" placeholder="PTAX/BCB" maxlength="60"></v-text-field></v-col>
                                </v-row>
                            </ui-section>
                        </v-col>
                    </v-row>
                    <div class="d-flex justify-end mt-4">
                        <v-btn v-if="$allowed('save')" color="primary" depressed :loading="busy === 'config'" @click="saveConfig">Salvar configuração</v-btn>
                    </div>
                </v-tab-item>

                <v-tab-item>
                    <plan-adhesion-plan-documents
                        :documents="campaign.plan_documents"
                        :upload-url="`/api/admin/plan-adhesions/${campaign.id}/plan-documents`"
                        :file-url="(doc) => `/api/admin/plan-adhesion-plan-documents/${doc.id}/file`"
                        :action-url="(doc, action) => `/api/admin/plan-adhesion-plan-documents/${doc.id}/${action}`"
                        can-review
                        upload-hint="O plano enviado pelo AJ é publicado na hora. Uma nova versão tira do quórum as adesões ao plano anterior até o credor assinar de novo."
                        @changed="load" />
                </v-tab-item>

                <v-tab-item>
                    <plan-adhesion-templates
                        mode="admin"
                        :templates="campaign.templates"
                        :current-template-id="campaign.current_template_id"
                        :catalog="catalog"
                        :class-names="campaign.class_names"
                        :voting-class-types="campaign.voting_class_types"
                        :urls="templateUrls"
                        @changed="load" />
                </v-tab-item>

                <v-tab-item>
                    <plan-adhesion-terms-review :campaign-id="campaign.id" :class-names="campaign.class_names" @changed="load" />
                </v-tab-item>

                <v-tab-item>
                    <plan-adhesion-analytics :urls="analyticsUrls" />
                </v-tab-item>

                <v-tab-item>
                    <div class="ui-list-bar">
                        <p class="ui-subtitle" style="margin: 0;">Toda ação do AJ, da recuperanda, dos credores e do sistema. Visível só para o AJ.</p>
                        <v-btn outlined @click="openNotifications"><v-icon left small>mdi-email-outline</v-icon>E-mails</v-btn>
                    </div>
                    <div class="ui-table-card">
                        <div class="ui-toolbar">
                            <v-autocomplete v-model="audit.filter.action" :items="auditActionItems" placeholder="Todas as ações" dense outlined hide-details clearable
                                class="ui-toolbar__field" style="max-width: 320px;" @change="loadAudit(1)"></v-autocomplete>
                            <v-select v-model="audit.filter.actor_type" :items="auditActorItems" placeholder="Todos os autores" dense outlined hide-details clearable
                                class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="loadAudit(1)"></v-select>
                            <v-btn text class="ui-toolbar__clear" :disabled="!audit.filter.action && !audit.filter.actor_type" @click="clearAuditFilters"><v-icon left small>mdi-restore</v-icon>Limpar</v-btn>
                        </div>
                        <v-data-table :headers="audit.headers" :items="audit.items" :loading="audit.loading" disable-sort disable-pagination hide-default-footer
                            no-data-text="Nenhum registro" loading-text="Carregando...">
                            <template v-slot:[`item.created_at`]="{ item }"><span style="white-space: nowrap;">{{ formatDateTime(item.created_at) }}</span></template>
                            <template v-slot:[`item.actor_type`]="{ item }">
                                <div class="ui-cell-main" style="font-weight: 400;">{{ authorLabels[item.actor_type] || item.actor_type }}</div>
                                <div v-if="item.actor_id" class="ui-cell-sub">#{{ item.actor_id }}</div>
                            </template>
                            <template v-slot:[`item.action`]="{ item }"><span class="ui-cell-main">{{ auditLabels[item.action] || item.action }}</span></template>
                            <template v-slot:[`item.payload`]="{ item }">
                                <span v-if="item.payload" class="ui-mono ui-muted" style="white-space: pre-wrap; word-break: break-all;">{{ JSON.stringify(item.payload) }}</span>
                            </template>
                        </v-data-table>
                        <ui-pagination :total="audit.total" :page="audit.options.page" :items-per-page="audit.options.itemsPerPage" :items-per-page-options="[25, 50, 100]"
                            @update:itemsPerPage="audit.options.itemsPerPage = $event" @update:page="loadAudit($event)" />
                    </div>

                    <v-dialog v-model="notifications.open" max-width="1000" scrollable>
                        <v-card class="ui-card">
                            <div class="ui-card__header">
                                <div>
                                    <h3 class="ui-section-title">E-mails da adesão</h3>
                                    <p class="ui-section-description">Enviados pela fila a cada minuto, com até 3 tentativas por destinatário.</p>
                                </div>
                                <div class="d-flex align-center" style="gap: 8px;">
                                    <v-btn v-if="$allowed('plan.notify')" outlined class="ui-btn-danger" :disabled="!notificationFailures" :loading="notifications.retrying" @click="retryNotifications">
                                        Reenviar falhas ({{ notificationFailures }})
                                    </v-btn>
                                    <v-btn icon @click="notifications.open = false"><v-icon>mdi-close</v-icon></v-btn>
                                </div>
                            </div>
                            <v-card-text class="pa-0" style="border-top: 1px solid #f1f1f3;">
                                <v-data-table :headers="notifications.headers" :items="notifications.items" :loading="notifications.loading" disable-sort
                                    :items-per-page="50" no-data-text="Nenhum e-mail na fila" :footer-props="{ itemsPerPageOptions: [50, 100, 200], itemsPerPageText: 'Itens por página:' }">
                                    <template v-slot:[`item.status`]="{ item }">
                                        <ui-badge set="adhesion" :status="item.status" />
                                        <div v-if="item.last_error" class="ui-small" style="color: #b91c1c;">{{ item.last_error }}</div>
                                    </template>
                                    <template v-slot:[`item.created_at`]="{ item }"><span class="ui-muted">{{ formatDateTime(item.sent_at || item.created_at) }}</span></template>
                                </v-data-table>
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                </v-tab-item>
            </v-tabs-items>
        </template>
    </div>
</template>

<script>
import axios from "axios";
import PlanAdhesionPlanDocuments from "@/components/plan-adhesion/PlanAdhesionPlanDocuments.vue";
import PlanAdhesionTemplates from "@/components/plan-adhesion/PlanAdhesionTemplates.vue";
import PlanAdhesionTermsReview from "@/components/plan-adhesion/PlanAdhesionTermsReview.vue";
import PlanAdhesionAnalytics from "@/components/plan-adhesion/PlanAdhesionAnalytics.vue";
import "@/components/plan-adhesion/plan-adhesion.scss";
import {
    STATUS_LABELS, STATUS_COLORS, AUTHOR_LABELS, errorMessage, formatDateTime, toLocalInput, fromLocalInput,
} from "@/components/plan-adhesion/plan-adhesion.util";

const AUDIT_LABELS = {
    campaign_created: "Campanha criada",
    campaign_updated: "Configuração alterada",
    campaign_opened: "Adesão aberta",
    campaign_closed: "Adesão encerrada",
    plan_uploaded: "Plano enviado",
    plan_approved: "Plano aprovado",
    plan_rejected: "Plano rejeitado",
    plan_published: "Plano publicado",
    plan_viewed: "Plano visualizado",
    template_created: "Versão do termo criada",
    template_updated: "Termo editado",
    template_submitted: "Termo enviado para aprovação",
    template_approved: "Termo aprovado",
    template_rejected: "Termo rejeitado",
    template_published: "Termo publicado",
    template_previewed: "Prévia do termo gerada",
    report_pdf_exported: "Laudo PDF exportado",
    report_xlsx_exported: "Planilha exportada",
    term_previewed: "Credor gerou prévia do termo",
    term_created: "Termo emitido",
    term_downloaded: "Termo baixado",
    signed_term_downloaded: "Termo assinado baixado",
    term_submitted: "Termo assinado enviado",
    term_auto_approved: "Assinatura GOV.br validada (aprovado)",
    term_auto_rejected: "Assinatura rejeitada na validação",
    term_sent_to_review: "Termo PJ enviado para análise",
    term_approved: "Adesão aprovada pelo AJ",
    term_rejected: "Termo rejeitado pelo AJ",
    term_revoked: "Adesão revogada",
    term_cancelled_credit_changed: "Cancelado: crédito alterado",
    term_credit_relinked: "Termo religado após reconsolidação",
    email_sent: "E-mail enviado",
    email_queued: "E-mails na fila",
};

export default {
    name: "PlanAdhesionDetails",

    components: { PlanAdhesionPlanDocuments, PlanAdhesionTemplates, PlanAdhesionTermsReview, PlanAdhesionAnalytics },

    data: () => ({
        campaign: null,
        catalog: [],
        tab: 0,
        busy: null,
        config: {},
        statusLabels: STATUS_LABELS,
        statusColors: STATUS_COLORS,
        authorLabels: AUTHOR_LABELS,
        auditLabels: AUDIT_LABELS,
        notifications: {
            open: false,
            loading: false,
            retrying: false,
            items: [],
            headers: [
                { text: "Data", value: "created_at", width: 140 },
                { text: "Tipo", value: "type" },
                { text: "Destinatário", value: "email" },
                { text: "Assunto", value: "subject" },
                { text: "Situação", value: "status" },
            ],
        },
        audit: {
            filter: { action: null, actor_type: null },
            items: [],
            total: 0,
            loading: false,
            options: { page: 1, itemsPerPage: 25 },
            headers: [
                { text: "Data", value: "created_at", width: 150 },
                { text: "Autor", value: "actor_type", width: 200 },
                { text: "Ação", value: "action", width: 220 },
                { text: "Detalhes", value: "payload" },
            ],
        },
    }),

    computed: {
        id() {
            return this.$route.params.id;
        },
        windowText() {
            const c = this.campaign;
            if (!c.opens_at) return "Janela de adesão não configurada";
            return `Adesão de ${formatDateTime(c.opens_at)} ${c.closes_at ? `até ${formatDateTime(c.closes_at)}` : "sem data de encerramento"}`;
        },
        analyticsUrls() {
            return {
                analytics: `/api/admin/plan-adhesions/${this.id}/analytics`,
                pdf: `/api/admin/plan-adhesions/${this.id}/report-pdf`,
                xlsx: `/api/admin/plan-adhesions/${this.id}/report-xlsx`,
            };
        },
        templateUrls() {
            return {
                create: `/api/admin/plan-adhesions/${this.id}/templates`,
                template: (templateId) => `/api/admin/plan-adhesion-templates/${templateId}`,
                action: (templateId, action) => `/api/admin/plan-adhesion-templates/${templateId}/${action}`,
            };
        },
        checklist() {
            const c = this.campaign;
            return [
                // A adesao nao exige QGC consolidado: usa o QGC vigente do caso.
                { label: c.qgc_version ? `QGC com créditos (v${c.qgc_version})` : "QGC com créditos", done: c.has_consolidated_qgc },
                { label: "Plano publicado", done: !!c.current_plan_document_id },
                { label: "Termo publicado", done: !!c.current_template_id },
                { label: "Adesão aberta", done: c.status === "open" },
            ];
        },
        auditActionItems() {
            return Object.entries(this.auditLabels).map(([value, text]) => ({ value, text })).sort((a, b) => a.text.localeCompare(b.text));
        },
        auditActorItems() {
            return Object.entries(this.authorLabels).map(([value, text]) => ({ value, text }));
        },
        notificationFailures() {
            return this.notifications.items.filter((item) => item.status === "failed").length;
        },
        pendingPlans() {
            return this.campaign.plan_documents.filter((doc) => doc.status === "pending_approval").length;
        },
        pendingTemplates() {
            return this.campaign.templates.filter((template) => template.status === "pending_approval").length;
        },
    },

    created() {
        this.load();
        this.loadCatalog();
    },

    methods: {
        formatDateTime,

        notifyError(error) {
            this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 5000 });
        },

        async load() {
            try {
                const result = await axios.get(`/api/admin/plan-adhesions/${this.id}`);
                this.campaign = result.data.content;
                this.config = {
                    opens_at: toLocalInput(this.campaign.opens_at),
                    closes_at: toLocalInput(this.campaign.closes_at),
                    exchange_rate_usd: this.campaign.exchange_rate_usd ? String(Number(this.campaign.exchange_rate_usd)).replace(".", ",") : "",
                    exchange_rate_eur: this.campaign.exchange_rate_eur ? String(Number(this.campaign.exchange_rate_eur)).replace(".", ",") : "",
                    exchange_rate_date: this.campaign.exchange_rate_date ? String(this.campaign.exchange_rate_date).slice(0, 10) : "",
                    exchange_rate_source: this.campaign.exchange_rate_source || "",
                };
            } catch (error) {
                this.notifyError(error);
            }
        },

        async loadCatalog() {
            try {
                const result = await axios.get("/api/admin/plan-adhesions/placeholders");
                this.catalog = result.data.content;
            } catch (error) {
                this.notifyError(error);
            }
        },

        async saveConfig() {
            this.busy = "config";
            try {
                await axios.put(`/api/admin/plan-adhesions/${this.id}`, {
                    opens_at: fromLocalInput(this.config.opens_at),
                    closes_at: fromLocalInput(this.config.closes_at),
                    exchange_rate_usd: this.config.exchange_rate_usd || null,
                    exchange_rate_eur: this.config.exchange_rate_eur || null,
                    exchange_rate_date: this.config.exchange_rate_date || null,
                    exchange_rate_source: this.config.exchange_rate_source || null,
                });
                this.$dialog.notify.success("Configuração salva.", { position: "top-right", timeout: 2500 });
                await this.load();
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        async confirmOpen() {
            const confirmed = await this.$dialog.confirm({
                title: "Abrir adesão",
                text: "Os credores com crédito no QGC vigente do caso poderão aderir ao plano pela área do credor. Confirma?",
                actions: { false: "Cancelar", true: { text: "Abrir adesão", color: "success" } },
            });
            if (!confirmed) return;
            await this.runAction("open", "Adesão aberta.");
        },

        async confirmClose() {
            const confirmed = await this.$dialog.confirm({
                title: "Encerrar adesão",
                text: "Novos termos deixam de ser aceitos. Os termos já enviados continuam em análise. Confirma?",
                actions: { false: "Cancelar", true: { text: "Encerrar", color: "error" } },
            });
            if (!confirmed) return;
            await this.runAction("close", "Adesão encerrada.");
        },

        async runAction(action, message) {
            this.busy = action;
            try {
                await axios.post(`/api/admin/plan-adhesions/${this.id}/${action}`);
                this.$dialog.notify.success(message, { position: "top-right", timeout: 2500 });
                await this.load();
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        async openNotifications() {
            this.notifications.open = true;
            this.notifications.loading = true;
            try {
                const result = await axios.get(`/api/admin/plan-adhesions/${this.id}/notifications`, { params: { limit: 1000 } });
                this.notifications.items = result.data.content;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.notifications.loading = false;
            }
        },

        async retryNotifications() {
            this.notifications.retrying = true;
            try {
                const result = await axios.post(`/api/admin/plan-adhesions/${this.id}/notifications/retry`);
                this.$dialog.notify.success(`${result.data.content.retried} e-mail(s) voltaram para a fila.`, { position: "top-right", timeout: 2500 });
                await this.openNotifications();
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.notifications.retrying = false;
            }
        },

        clearAuditFilters() {
            this.audit.filter = { action: null, actor_type: null };
            this.loadAudit(1);
        },

        async loadAudit(page = null) {
            if (page) this.audit.options = { ...this.audit.options, page };
            this.audit.loading = true;
            try {
                const result = await axios.get(`/api/admin/plan-adhesions/${this.id}/audit-logs`, {
                    params: {
                        page: this.audit.options.page,
                        limit: this.audit.options.itemsPerPage,
                        action: this.audit.filter.action || undefined,
                        actor_type: this.audit.filter.actor_type || undefined,
                    },
                });
                this.audit.items = result.data.content.data;
                this.audit.total = Number(result.data.content.total) || 0;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.audit.loading = false;
            }
        },
    },
};
</script>
